import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Film } from 'lucide-react';
import { AdminCrudShell, StatusBadge } from '@/components/admin/AdminCrudShell';
import { fetchAdminList, fetchSports } from '@/lib/api';

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Borrador' },
  { value: 'live_scheduled', label: 'Próximo directo' },
  { value: 'published', label: 'Publicado' },
];

// Catálogo canónico compartido con Equipos (misma lista que `TeamCategory`
// en backend y que la usada en `AdminTeams`). Guardamos el identificador en
// minúscula sin acentos ni plurales; el label es sólo presentación.
const EVENT_CATEGORY_CANONICAL = [
  { value: 'senior', label: 'Senior' },
  { value: 'juvenil', label: 'Juvenil' },
  { value: 'cadete', label: 'Cadete' },
  { value: 'infantil', label: 'Infantil' },
  { value: 'alevin', label: 'Alevín' },
  { value: 'benjamin', label: 'Benjamín' },
];

// Al editar un partido antiguo cuya categoría no cuadra con el catálogo
// canónico (ej. "Benjamines", "Senior femenino") NO forzamos migración: se
// añade como opción heredada para preservar el valor y no romper listados
// ni filtros existentes.
function categoryOptions(row) {
  const cur = String((row && row.category) || '').trim();
  if (!cur) return EVENT_CATEGORY_CANONICAL;
  const canonical = EVENT_CATEGORY_CANONICAL.some((o) => o.value === cur);
  if (canonical) return EVENT_CATEGORY_CANONICAL;
  return [
    ...EVENT_CATEGORY_CANONICAL,
    { value: cur, label: `${cur} (heredada)` },
  ];
}

// Nota: ACCESS_OPTIONS retirado de la UI de partido en Entrega 1.
// El acceso/precio se gestiona ahora a nivel de Contenido (ver /admin/videos
// o, desde Entrega 2, pestaña Contenidos en la ficha del partido).

function toLocalDatetimeInput(value) {
  if (!value) return '';
  try {
    const d = new Date(value);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ''; }
}

function toISO(localStr) {
  if (!localStr) return null;
  try { return new Date(localStr).toISOString(); } catch { return localStr; }
}

/**
 * Lanza un error con la forma esperada por `extractFieldErrors` para que
 * la validación cliente se pinte igual que un error del backend (toast
 * específico + mensaje inline bajo el campo).
 */
function throwFieldValidationError(field, message, type = 'client_validation') {
  const err = new Error(message);
  err.response = {
    status: 422,
    data: {
      success: false,
      error: 'validation_error',
      message,
      data: [{ loc: ['body', field], msg: message, type }],
    },
  };
  throw err;
}

function EventRowContentsAction({ row }) {
  return (
    <Button asChild size="sm" variant="ghost" className="h-8 px-2 text-[var(--ns-gold-700)] hover:bg-[var(--ns-gold)]/15" data-testid={`admin-events-contents-${row.id}`}>
      <Link to={`/admin/eventos/${row.id}/contenidos`}>
        <Film className="h-3.5 w-3.5 mr-1" /> Contenidos
      </Link>
    </Button>
  );
}

export default function AdminEvents() {
  const [sports, setSports] = useState([]);
  const [clubs, setClubs] = useState([]);
  // Estado del filtro Deporte (necesario para computar dependientemente
  // las opciones del filtro Club). Se pasa además a extraQueryParams para
  // que la lista se recargue cuando cambia.
  const [sportId, setSportId] = useState('');

  useEffect(() => {
    fetchSports().then(setSports).catch(() => setSports([]));
    fetchAdminList('clubs', {}).then((res) => setClubs(res.items || [])).catch(() => setClubs([]));
  }, []);
  const sportsOptions = sports.map((s) => ({ value: s.id, label: s.name }));
  const clubsOptions = clubs.map((c) => ({ value: c.id, label: c.name }));
  const clubNameById = (id) => clubs.find((c) => c.id === id)?.name || '—';

  // Clubes dependientes: cuando hay un deporte seleccionado, sólo mostramos
  // los clubes de ese deporte. Reutiliza la relación existente club.sport_id.
  const dependentClubsOptions = React.useMemo(() => {
    if (!sportId) return clubsOptions;
    return clubs
      .filter((c) => c.sport_id === sportId)
      .map((c) => ({ value: c.id, label: c.name }));
  }, [clubs, clubsOptions, sportId]);

  return (
    <AdminCrudShell
      title="Partidos"
      subtitle="Crea, edita, publica, archiva o cancela partidos. El acceso y precio se gestionan desde los Contenidos."
      entity="events"
      testIdPrefix="admin-events"
      searchPlaceholder="Buscar por título, equipos o club…"
      filters={[
        { name: 'sport_id', label: 'Deporte', options: sportsOptions,
          onChange: (v) => setSportId(v) },
        { name: 'club_id', label: 'Club', options: dependentClubsOptions },
      ]}
      initialForm={{
        title: '', slug: '', description: '',
        sport_id: '', club_id: '', participant_club_id: '',
        event_date: '', country: 'España',
        autonomous_community_or_region: '', province: '', city: '',
        venue: '', category: '',
        thumbnail_url: '', trailer_url: '', preview_video_url: '',
        status: 'draft',
      }}
      beforeSubmit={(payload) => {
        // Validación cliente: `event_date` debe incluir hora completa
        // (`YYYY-MM-DDTHH:MM`). Si falta la parte de la hora, mostramos
        // un error específico junto al campo (reutilizando la utilidad
        // genérica de errores de validación).
        const raw = (payload.event_date || '').toString();
        if (payload.event_date) {
          const hasTime = /T\d{2}:\d{2}/.test(raw);
          if (!hasTime) {
            throwFieldValidationError(
              'event_date',
              'Falta completar la hora del partido.',
              'time',
            );
          }
        }
        return { ...payload, event_date: toISO(payload.event_date) };
      }}
      fieldErrorOverrides={{
        // Mensaje específico y amigable (anula la heurística genérica).
        event_date: 'Falta completar la hora del partido.',
      }}
      columns={[
        {
          key: 'title',
          label: 'Partido',
          // Columna principal: crece con el espacio disponible. Sin ellipsis
          // ni max-w — permite 2-3 líneas si el título es largo.
          className: 'min-w-[180px] w-[38%] whitespace-normal break-words',
          render: (r) => (
            <div className="font-medium text-[var(--ns-text)] whitespace-normal break-words leading-snug">
              {r.title}
            </div>
          ),
        },
        {
          key: 'club_id', label: 'Club',
          className: 'min-w-[120px] w-[18%] whitespace-normal break-words',
          render: (r) => (
            <span className="whitespace-normal break-words leading-snug">
              {clubNameById(r.club_id)}
            </span>
          ),
        },
        { key: 'event_date', label: 'Fecha',
          className: 'whitespace-nowrap w-[14%]',
          render: (r) => r.event_date ? new Date(r.event_date).toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' }) : '—' },
        { key: 'category', label: 'Categoría',
          className: 'whitespace-nowrap w-[10%]',
          render: (r) => r.category || '—' },
        { key: 'status', label: 'Estado',
          className: 'whitespace-nowrap w-[10%]',
          render: (r) => (
            <span className="inline-block px-2 py-1 rounded-full text-[10px] uppercase tracking-wider font-bold bg-[var(--ns-bg-2)] text-[var(--ns-text-muted)] border border-[var(--ns-border)]">{r.status}</span>
          ) },
      ]}
      formFields={[
        { name: 'title', label: 'Título del partido', required: true, placeholder: 'Ej: Chiclana C.F. vs Conil C.F.' },
        { name: 'sport_id', label: 'Deporte', type: 'select', required: true, options: sportsOptions },
        { name: 'club_id', label: 'Club propietario / publicador', type: 'select', required: true, options: clubsOptions, hint: 'Determina qué Premium Club concede acceso a los contenidos premium asociados.' },
        { name: 'participant_club_id', label: 'Otro club NetScouters participante', type: 'select', options: clubsOptions, hint: 'Opcional. Si el segundo club también es cliente de NetScouters, el partido aparecerá también en su ficha pública. Si no lo es, deja vacío.' },
        { name: 'event_date', label: 'Fecha y hora', type: 'datetime-local', required: true },
        { name: 'country', label: 'País', required: true },
        { name: 'autonomous_community_or_region', label: 'Comunidad / Región' },
        { name: 'province', label: 'Provincia' },
        { name: 'city', label: 'Ciudad' },
        { name: 'venue', label: 'Sede / Instalación', placeholder: 'Estadio Municipal…' },
        { name: 'category', label: 'Categoría', type: 'select', required: false, options: categoryOptions, hint: 'Mismo catálogo que Equipos. Los partidos antiguos con categoría personalizada conservan su valor como opción heredada.' },
        { name: 'thumbnail_url', label: 'Miniatura del partido', type: 'image', uploadKind: 'thumbnail', aspectClass: 'aspect-video', hint: 'JPG o PNG. Se ajustará a 16:9 (máx. 1280×720).' },
        { name: 'preview_video_url', label: 'URL MP4 de vista previa (opcional)', placeholder: 'https://cdn.ejemplo.com/preview.mp4', hint: 'Preview público del partido. Recomendado: 20–30 segundos.' },
        { name: 'preview_views', label: 'Reproducciones del preview', type: 'readonly', format: (v) => Number(v || 0).toLocaleString('es-ES') },
        { name: 'status', label: 'Estado', type: 'select', required: true, options: STATUS_OPTIONS, hint: 'Para publicar, indica provincia o ciudad mínimo.' },
      ]}
      rowActions={EventRowContentsAction}
    />
  );
}
